import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { AuthContext } from "../context/AuthContext";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  CpuChipIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import {
  checkEcu,
  downloadBlob,
  dtcRowToPairs,
  extractApiError,
  extractDtcRows,
  hasDtc2Column,
  patchEcu,
} from "../utils/automaticApi";
import { SolutionIcon, TuningIcon } from "../utils/moduleIcons";

const priceLabel = (credits) => (credits == null ? "n/a" : `${credits} CRS`);

const primaryBtn =
  "inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-5 py-2.5 font-semibold text-white shadow-lg shadow-red-500/30 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400 disabled:shadow-none";

const Spinner = ({ light }) => (
  <svg className={`h-5 w-5 animate-spin ${light ? "text-white" : "text-red-500"}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

// Toggle switch. Green = default (unchanged); red = toggled on for the patch.
const Switch = ({ checked, disabled, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition disabled:opacity-40 ${
      checked ? "bg-emerald-500" : "bg-red-500"
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
        checked ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

const Panel = ({ icon, title, count, action, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white/90 shadow-xl shadow-gray-200/40 backdrop-blur dark:border-gray-700 dark:bg-gray-800/80 dark:shadow-black/20">
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-red-500/15 to-orange-500/15 text-red-600 dark:text-orange-400">
          {icon}
        </span>
        <h4 className="text-base font-bold text-gray-900 dark:text-gray-100">
          {title}
          {count != null ? <span className="ml-2 text-sm font-medium text-gray-400">{count}</span> : null}
        </h4>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EmptyHint = ({ text }) => <p className="py-8 text-center text-sm text-gray-400">{text}</p>;

// A solution/tuning row. `active` = toggled on for the patch (red); default is green.
const ToggleTile = ({ label, price, active, disabled, onToggle, icon }) => (
  <div
    className={`flex items-center gap-3 rounded-xl border p-3 transition ${
      active
        ? "border-red-500 bg-red-50 dark:bg-red-500/10"
        : disabled
        ? "border-gray-200 bg-gray-50 opacity-60 dark:border-gray-700 dark:bg-gray-900/40"
        : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    }`}
  >
    {icon ? (
      <span
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
          active
            ? "bg-red-500/15 text-red-600 dark:text-red-400"
            : "bg-gradient-to-br from-red-500/10 to-orange-500/10 text-red-600 dark:text-orange-400"
        }`}
      >
        {icon}
      </span>
    ) : null}
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{label}</p>
      <span
        className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
          price == null
            ? "bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-400"
            : "bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 dark:text-orange-400"
        }`}
      >
        {priceLabel(price)}
      </span>
    </div>
    <Switch checked={!active} disabled={disabled} onToggle={onToggle} />
  </div>
);

const GroupLabel = ({ children }) => (
  <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">{children}</p>
);

const Automatic = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [brand, setBrand] = useState("");
  const [ecu, setEcu] = useState("");

  const [checking, setChecking] = useState(false);
  const [quote, setQuote] = useState(null);
  const [needsSelection, setNeedsSelection] = useState(false);
  const [error, setError] = useState("");

  const [selectedModules, setSelectedModules] = useState(() => new Set());
  const [selectedTunings, setSelectedTunings] = useState(() => new Set());
  const [dtcOffSet, setDtcOffSet] = useState(() => new Set());
  const [dtcSearch, setDtcSearch] = useState("");

  const [patching, setPatching] = useState(false);
  const [patchResult, setPatchResult] = useState(null);

  const [creditBalance, setCreditBalance] = useState(null);
  const [modalMsg, setModalMsg] = useState("");
  const { user } = useContext(AuthContext);
  const unlimited = String(user?.role || "").toLowerCase() === "admin";

  const refreshBalance = () =>
    axiosInstance
      .get("/users/my")
      .then((res) => setCreditBalance(Number(res.data?.credit_balance ?? 0)))
      .catch(() => {});
  useEffect(() => {
    refreshBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = checking || patching;

  const info = quote?.identification?.ecuinfo || quote?.identification?.ecuInfo || {};
  const pricing = quote?.pricing || {};
  const moduleEntries = useMemo(() => Object.entries(pricing.modules || {}), [pricing.modules]);
  const tunings = pricing.tunings || [];
  const dtcRows = useMemo(() => extractDtcRows(quote?.identification?.dtcTable), [quote]);
  const hasDtc2 = useMemo(() => hasDtc2Column(dtcRows), [dtcRows]);
  // Keep the original index so the dtcOffSet (keyed by row index) stays valid while filtering.
  const visibleDtcRows = useMemo(() => {
    const rows = dtcRows.map((row, index) => ({ row, index }));
    const q = dtcSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      ({ row }) =>
        (row.code1 || "").toLowerCase().includes(q) ||
        (row.code2 || "").toLowerCase().includes(q) ||
        (row.description || "").toLowerCase().includes(q)
    );
  }, [dtcRows, dtcSearch]);
  const swNumber =
    info.softwareNumber ||
    (Array.isArray(info.softwareNumbers) ? info.softwareNumbers[0] : "") ||
    "";

  const dtcActive = dtcOffSet.size > 0;
  const dtcSelectedPairs = useMemo(
    () => Array.from(dtcOffSet).flatMap((i) => dtcRowToPairs(dtcRows[i])),
    [dtcOffSet, dtcRows]
  );

  const totalCost = useMemo(() => {
    let total = 0;
    for (const [name, credits] of moduleEntries) {
      if (selectedModules.has(name) && credits != null) total += credits;
    }
    for (const tuning of tunings) {
      if (selectedTunings.has(tuning.id) && tuning.credits != null) total += tuning.credits;
    }
    if (dtcActive && pricing.dtcOff != null) total += pricing.dtcOff;
    return total;
  }, [moduleEntries, tunings, selectedModules, selectedTunings, dtcActive, pricing.dtcOff]);

  const nothingSelected = selectedModules.size === 0 && selectedTunings.size === 0 && !dtcActive;
  const dtcDisabled = pricing.dtcOff == null;

  const infoFields = [
    // /check reports the identified pair as ecuinfo.systemManufacturer (brand) and
    // ecuinfo.ecuId (ECU) — the same two values /patch takes as `brand` and `ecu`.
    ["Brand", info.systemManufacturer],
    ["Model", info.vehicleModel],
    ["ECU", info.ecuId],
    ["Software #", swNumber],
    ["Hardware #", info.hardwareNumber],
    ["Engine", info.engineDesignation],
    ["Power", info.enginePowerKw ? `${info.enginePowerKw} kW` : ""],
    ["Torque", info.engineTorque ? `${info.engineTorque} Nm` : ""],
    ["Emission", info.emissionStandard],
    ["Year", info.vehicleYear ? String(info.vehicleYear) : ""],
  ].filter(([, v]) => v);

  const resetResults = () => {
    setQuote(null);
    setPatchResult(null);
    setNeedsSelection(false);
    setSelectedModules(new Set());
    setSelectedTunings(new Set());
    setDtcOffSet(new Set());
  };

  const runCheck = async (theFile, theBrand, theEcu) => {
    if (!theFile) return;
    setError("");
    setChecking(true);
    setQuote(null);
    setPatchResult(null);
    try {
      const result = await checkEcu({ file: theFile, brand: theBrand, ecu: theEcu });
      setQuote(result);
      setNeedsSelection(false);
    } catch (err) {
      if (err?.response?.status === 409) {
        setNeedsSelection(true);
        setError("Multiple ECUs match this file. Enter the brand and ECU, then identify again.");
      } else {
        setError(await extractApiError(err, "Identification failed. Please try again."));
      }
    } finally {
      setChecking(false);
    }
  };

  const handleSelectedFile = (nextFile) => {
    if (!nextFile) return;
    setFile(nextFile);
    setBrand("");
    setEcu("");
    resetResults();
    runCheck(nextFile, "", "");
  };

  const handleDragOver = (event) => event.preventDefault();
  const handleDrop = (event) => {
    event.preventDefault();
    if (busy) return;
    handleSelectedFile(event.dataTransfer.files?.[0] || null);
  };

  const flip = (setFn) => (key) =>
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleModule = flip(setSelectedModules);
  const toggleTuning = flip(setSelectedTunings);
  const toggleDtc = flip(setDtcOffSet);

  // Only allow turning something on if the customer can afford the added cost;
  // otherwise pop the appropriate modal. Turning things off is always allowed.
  const requestSelect = (addedCost, apply) => {
    if (unlimited || addedCost <= 0 || creditBalance == null) return apply();
    if (creditBalance <= 0) return setModalMsg("spent");
    if (totalCost + addedCost > creditBalance) return setModalMsg("notEnough");
    return apply();
  };
  const onToggleModule = (name) =>
    selectedModules.has(name)
      ? toggleModule(name)
      : requestSelect(pricing.modules?.[name] ?? 0, () => toggleModule(name));
  const onToggleTuning = (tuning) =>
    selectedTunings.has(tuning.id)
      ? toggleTuning(tuning.id)
      : requestSelect(tuning.credits ?? 0, () => toggleTuning(tuning.id));
  const onToggleDtcRow = (index) =>
    dtcOffSet.has(index)
      ? toggleDtc(index)
      : requestSelect(dtcActive ? 0 : pricing.dtcOff ?? 0, () => toggleDtc(index));

  const handlePatch = async () => {
    if (nothingSelected) {
      setError("Toggle at least one solution, tuning, or DTC.");
      return;
    }
    if (!unlimited && creditBalance != null && totalCost > creditBalance) {
      setModalMsg(creditBalance <= 0 ? "spent" : "notEnough");
      return;
    }
    setError("");
    setPatching(true);
    setPatchResult(null);
    try {
      const result = await patchEcu({
        file,
        brand: brand || info.systemManufacturer,
        ecu: ecu || info.ecuId,
        swNumber,
        modules: Array.from(selectedModules),
        tuningIds: Array.from(selectedTunings),
        dtcPairs: dtcSelectedPairs,
      });
      downloadBlob(result.blob, result.fileName);
      setPatchResult({ fileName: result.fileName, charged: result.charged, balance: result.balance });
      refreshBalance();
    } catch (err) {
      setError(await extractApiError(err, "Patch failed. Please try again."));
    } finally {
      setPatching(false);
    }
  };

  const statusText = checking
    ? "Identifying file…"
    : patching
    ? "Patching file…"
    : !file
    ? "Upload an ECU file to identify it — free. You only pay when you patch."
    : quote
    ? "Toggle what to change, then patch."
    : "Ready.";

  const selectionCount = selectedModules.size + selectedTunings.size + (dtcActive ? 1 : 0);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" onDragOver={handleDragOver} onDrop={handleDrop}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => handleSelectedFile(e.target.files?.[0] || null)}
      />

      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Compact upload control */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white/90 px-5 py-4 shadow-xl shadow-gray-200/40 backdrop-blur dark:border-gray-700 dark:bg-gray-800/80 dark:shadow-black/20">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-white ${
              file && !checking ? "bg-gradient-to-br from-emerald-500 to-green-500" : "bg-gradient-to-br from-red-500 to-orange-500"
            }`}
          >
            {checking ? <Spinner light /> : file ? <DocumentIcon className="h-6 w-6" /> : <ArrowUpTrayIcon className="h-6 w-6" />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {file ? file.name : "No file selected"}
            </p>
            <p className="truncate text-xs text-gray-400">{statusText}</p>
          </div>
        </div>
        <button type="button" disabled={busy} onClick={() => fileInputRef.current?.click()} className={primaryBtn}>
          <ArrowUpTrayIcon className="h-5 w-5" />
          {file ? "Change file" : "Upload file"}
        </button>
      </div>

      {/* Brand / ECU disambiguation */}
      {needsSelection && (
        <div className="mb-6 rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-xl dark:border-gray-700 dark:bg-gray-800/80">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-48">
              <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Brand</label>
              <input
                className="w-full rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
            </div>
            <div className="w-48">
              <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">ECU</label>
              <input
                className="w-full rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                value={ecu}
                onChange={(e) => setEcu(e.target.value)}
              />
            </div>
            <button type="button" disabled={checking} onClick={() => runCheck(file, brand, ecu)} className={primaryBtn}>
              {checking ? <Spinner light /> : <SparklesIcon className="h-5 w-5" />}
              {checking ? "Identifying…" : "Identify again"}
            </button>
          </div>
        </div>
      )}

      {/* Layout (always shown) */}
      <div className="space-y-6">
        <Panel
          icon={<SparklesIcon className="h-5 w-5" />}
          title="Solutions & Tuning"
          count={quote && selectionCount > 0 ? `${selectionCount} selected` : null}
        >
          {!quote ? (
            <EmptyHint text="Upload an ECU file to see the available solutions & tunings." />
          ) : moduleEntries.length === 0 && tunings.length === 0 ? (
            <p className="text-sm text-gray-500">No solutions or tunings available for this file.</p>
          ) : (
            <>
              {moduleEntries.length > 0 && (
                <div className="mb-5">
                  <GroupLabel>Solutions</GroupLabel>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {moduleEntries.map(([name, credits]) => (
                      <ToggleTile
                        key={name}
                        label={name}
                        price={credits}
                        disabled={credits == null}
                        active={selectedModules.has(name)}
                        onToggle={() => onToggleModule(name)}
                        icon={<SolutionIcon name={name} className="h-5 w-5" />}
                      />
                    ))}
                  </div>
                </div>
              )}
              {tunings.length > 0 && (
                <div>
                  <GroupLabel>Tunings</GroupLabel>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {tunings.map((tuning) => (
                      <ToggleTile
                        key={tuning.id}
                        label={tuning.name}
                        price={tuning.credits}
                        disabled={tuning.credits == null}
                        active={selectedTunings.has(tuning.id)}
                        onToggle={() => onToggleTuning(tuning)}
                        icon={<TuningIcon name={tuning.name} className="h-5 w-5" />}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Panel>

        <div className="grid gap-6 lg:grid-cols-2">
          <Panel
            icon={<ShieldExclamationIcon className="h-5 w-5" />}
            title="DTC Table"
            count={dtcRows.length > 0 ? dtcRows.length : null}
            action={
              dtcRows.length > 0 ? (
                <input
                  type="text"
                  value={dtcSearch}
                  onChange={(e) => setDtcSearch(e.target.value)}
                  placeholder="Search DTC…"
                  className="w-32 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 placeholder-gray-400 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 sm:w-44"
                />
              ) : null
            }
          >
            {!quote ? (
              <EmptyHint text="DTCs will appear here after identification." />
            ) : dtcRows.length === 0 ? (
              <p className="text-sm text-gray-500">No DTCs found in this file.</p>
            ) : (
              <div className="max-h-[420px] overflow-y-auto rounded-xl border border-gray-100 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-100 text-left text-xs uppercase tracking-wider text-gray-400 shadow-sm dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Off</th>
                      <th className="px-4 py-2 font-semibold">{hasDtc2 ? "DTC1" : "DTC"}</th>
                      {hasDtc2 && <th className="px-4 py-2 font-semibold">DTC2</th>}
                      <th className="px-4 py-2 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDtcRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={hasDtc2 ? 4 : 3}
                          className="px-4 py-6 text-center text-sm text-gray-400"
                        >
                          No DTCs match “{dtcSearch}”.
                        </td>
                      </tr>
                    ) : null}
                    {visibleDtcRows.map(({ row, index }) => {
                      const off = dtcOffSet.has(index);
                      return (
                        <tr
                          key={`${row.code1}-${row.code2}-${index}`}
                          className={`border-t transition ${
                            off
                              ? "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10"
                              : "border-gray-100 dark:border-gray-700"
                          }`}
                        >
                          <td className="px-4 py-1.5">
                            <Switch
                              checked={!off}
                              disabled={dtcDisabled}
                              onToggle={() => onToggleDtcRow(index)}
                            />
                          </td>
                          <td className={`px-4 py-1.5 font-mono text-xs ${off ? "text-rose-600 dark:text-rose-300" : "text-gray-800 dark:text-gray-200"}`}>
                            {row.code1 || "—"}
                          </td>
                          {hasDtc2 && (
                            <td className={`px-4 py-1.5 font-mono text-xs ${off ? "text-rose-600 dark:text-rose-300" : "text-gray-800 dark:text-gray-200"}`}>
                              {row.code2 || "—"}
                            </td>
                          )}
                          <td className="px-4 py-1.5 text-gray-600 dark:text-gray-400">{row.description || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel icon={<CpuChipIcon className="h-5 w-5" />} title="File Identification">
            {!quote ? (
              <EmptyHint text="File details will appear here after identification." />
            ) : infoFields.length === 0 ? (
              <p className="text-sm text-gray-500">No identification details.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {infoFields.map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
                    <p className="mt-0.5 truncate text-sm font-semibold text-gray-800 dark:text-gray-100" title={value}>
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {quote && (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-white/90 px-5 py-4 shadow-xl shadow-gray-200/40 backdrop-blur dark:border-gray-700 dark:bg-gray-800/80 dark:shadow-black/20">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total
                <span className="ml-2 bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-2xl font-extrabold text-transparent">
                  {totalCost}
                </span>
                <span className="ml-1 text-sm font-medium text-gray-500">CRS</span>
                {selectionCount > 0 && (
                  <span className="ml-2 text-xs text-gray-400">
                    · {selectionCount} selected
                  </span>
                )}
              </p>
              {patchResult && (
                <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                  <CheckIcon className="h-4 w-4" />
                  Patched “{patchResult.fileName}” — downloaded successfully.
                </p>
              )}
            </div>
            <button type="button" onClick={handlePatch} disabled={patching || nothingSelected} className={primaryBtn}>
              {patching ? <Spinner light /> : <ArrowDownTrayIcon className="h-5 w-5" />}
              {patching ? "Patching…" : "Patch & download"}
            </button>
          </div>
        )}
      </div>

      {modalMsg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setModalMsg("")}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15">
              <ExclamationTriangleIcon className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {modalMsg === "spent" ? "You spent all your credits" : "Not enough credits"}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {modalMsg === "spent"
                ? "You have no credits left. Buy more to continue."
                : "You don't have enough credits for this selection."}
            </p>
            <div className="mt-5 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setModalMsg("")}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Close
              </button>
              <Link to="/buy-credits" onClick={() => setModalMsg("")} className={primaryBtn}>
                Buy credits
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Automatic;
